import { Col, Container, Placeholder, Row } from 'react-bootstrap'
import React, { useMemo } from 'react'
import { MeetingType } from './MeetingType'
import { MeetingDateAndTime } from '../../common/MeetingDateAndTime'
import { EditableText } from '../../common/EditableText/EditableText'
import { parseMetadata } from '../utils'
import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession, selectRoomSessionIsLoading, selectRoomSessionMeetingTitle } from '../../../../../store/meetingSummary/roomSession'
import { useUpdateRoomSessionMetadata } from '../hooks/api'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { PrivacyControl } from './PrivacyControl'
import { PrivateBadge } from '../../../../../views/LiveMeetings/PrivateBadge'
import { usePrivateMeetingsEnabled } from '../../../../../common/usePrivateMeetingsEnabled'
import './MeetingInfo.scss'
import { CreatorType } from './CreatorType'
import { MeetingLanguageDisplay } from './Language'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useMultiLanguageEnabled } from '../../../../../common/useMultiLanguageEnabled'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { getLanguageSettingPastDescription } from '../../../../../views/LanguageSupport/languageDescriptionUtils'
import { LanguageConfigSource, MeetingLanguage } from '../../../../../models/Room'

export const MeetingInfo = () => {
  const { tracked } = useTracking()
  const roomSession = useAppSelector(selectRoomSession)
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const meetingTitle = useAppSelector(selectRoomSessionMeetingTitle)
  const updateMetadata = useUpdateRoomSessionMetadata()
  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()
  const { multiLanguageEnabled } = useMultiLanguageEnabled()
  const language = roomSession?.config?.language ?? { language: MeetingLanguage.ENGLISH, source: LanguageConfigSource.DEFAULT }

  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')

  const { creator_service_id: creatorServiceId, source } = parseMetadata(roomSession?.metadata)

  const meetingType = useMemo(() => {
    if (!roomSession) {
      return {}
    }
    return source ?? ''
  }, [roomSession, source])

  if (!roomSession || roomSessionIsLoading) {
    return (
      <Container className='zocks-mt-3.5 mx-0 w-100 mw-100 p-0'>
        <Row className='p-0'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={4} bg={'light'} />
          </Placeholder>
        </Row>
        <Row className='mt-3 d-flex flex-column zocks-gap-1.25'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={2} bg={'light'} />
          </Placeholder>
        </Row>
      </Container>
    )
  }

  const { startedAt, finishedAt } = roomSession

  const _handleMeetingTitleChanged = async (newTitle: string) => {
    if (!roomSession) {
      return
    }

    const currentMetadata = parseMetadata(roomSession.metadata)
    const { external } = currentMetadata
    await updateMetadata({ ...external, title: newTitle })
  }

  const handleMeetingTitleChanged = tracked(
    _handleMeetingTitleChanged,
    { eventName: TrackEvent.ROOM_SESSION_RENAMED, eventDetails: getRoomTrackingDetails(roomSession) },
    {
      eventDetailsExtractor: (newTitle: string) => ({
        meetingTitle: newTitle,
      }),
    },
  )

  const languageTooltip = language ? getLanguageSettingPastDescription(language) : null

  return (
    <Container className='mx-0 p-0 mw-100 w-100'>
      <Row>
        <Col className='p-0' xs='6'>
          <EditableText
            text={meetingTitle}
            viewElement={({ children }) => <h3 className='p-0 mb-2'>{children}</h3>}
            textOverflow={false}
            hoverIcon
            rows={1}
            editIcon='bi-pencil'
            onChange={handleMeetingTitleChanged}
            missingScopeAction={MissingScopeAction.DISABLE}
            scope={{ scope: 'room_session:update', superScope: ['room_session:admin'] }}
          />
        </Col>
      </Row>
      <Row className='d-flex align-items-center mt-3  gap-2 gap-sm-3'>
        {privateMeetingsEnabled || roomSession.isPrivate ? (
          <Col xs={12} sm='auto' className='p-0 meeting-summary-privacy'>
            {privateMeetingsEnabled ? <PrivacyControl session={roomSession} /> : roomSession.isPrivate ? <PrivateBadge /> : null}
          </Col>
        ) : null}
        <Col xs={12} sm='auto' className='p-0'>
          <MeetingDateAndTime startedAt={startedAt} finishedAt={finishedAt} />
        </Col>
        {hasMultiLanguageSupportQuota && multiLanguageEnabled && language && language.language != MeetingLanguage.AUTO_DETECT ? (
          <Col xs={12} sm='auto' className='p-0'>
            {languageTooltip ? (
              <OverlayTrigger placement='top' overlay={<Tooltip id='meeting-language-tooltip'>{languageTooltip}</Tooltip>}>
                <span>
                  <MeetingLanguageDisplay language={language?.language} shortLabel={true} />
                </span>
              </OverlayTrigger>
            ) : (
              <MeetingLanguageDisplay language={language?.language} shortLabel={true} />
            )}
          </Col>
        ) : null}
        {creatorServiceId ? (
          <Col xs={12} sm='auto' className='p-0'>
            <CreatorType creator={creatorServiceId} />
          </Col>
        ) : null}
        <Col xs={12} sm='auto' className='p-0'>
          <MeetingType meetingType={meetingType} />
        </Col>
      </Row>
    </Container>
  )
}
